import { useCallback, useEffect, useRef, useState } from 'react';
import { CloudUpload } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { beginMigrationUpload } from '../hooks/use-migration-upload-activity';
import { useRawMultipartAdapter } from '../hooks/use-raw-multipart-adapter';
import type { PresignedRawUploadFile } from '../store/hooks';
import { usePresignRawUploadsMutation, useRefreshRawUploadInventoryMutation } from '../store/hooks';
import {
  shouldUseMultipart,
  UploadCancelledError,
  uploadMultipart,
  xhrPutPart,
} from '../utils/multipart-upload';
import type { UploadCounts, UploadOutcome, UploadStop } from '../utils/raw-upload-outcome';
import {
  describeUploadError,
  isBatchRefusal,
  isPermanentPresignRefusal,
  readPresignBlockReason,
  S3UploadError,
  s3ErrorFromResponse,
  uploadFailureGuidance,
  uploadOutcomeHeadline,
  uploadProgressText,
} from '../utils/raw-upload-outcome';
import { RawUploadOutcomeAlert } from './raw-upload-outcome-alert';

interface UploadFile {
  file: File;
  path: string;
}

/** A presigned URL paired with the file it uploads. */
interface UploadTarget {
  meta: PresignedRawUploadFile;
  file: File;
}

interface OrganizationDataImportV2SectionProps {
  organizationId: string;
  /**
   * Called after an upload that landed something, so the imports list can
   * refresh. `complete` is false when files failed or the run stopped early:
   * a host that would close or unmount the uploader should keep it open then,
   * or the failure alert disappears with it (GDN-4124).
   */
  onUploaded?: (result: { complete: boolean }) => void;
  /**
   * When set, append the files to this existing batch (raw upload id) instead
   * of creating a new one. Allowed only while the batch is still pre-conversion.
   */
  uploadId?: string;
  /** Render just the drop zone, without the surrounding Card chrome (e.g. in a dialog). */
  embedded?: boolean;
}

/** How many direct-to-S3 PUTs to run at once (folder drops can be hundreds of files). */
const MAX_CONCURRENT_UPLOADS = 8;

/**
 * How many filenames to presign per request. Presigned PUT URLs expire 15
 * minutes after minting, so a large drop must be presigned in waves sized to
 * upload well inside that window — presigning everything up front hands most
 * of a big drop dead URLs (GDN-2808). One wave is also small enough that the
 * presign request/response stays far from the gateway limits.
 */
const PRESIGN_WAVE_SIZE = 400;

/**
 * Byte cap per wave, so a wave of large files can't outlive the 15-minute URL
 * TTL just because 400 of them add up to gigabytes. ~256MB finishes inside the
 * window even on modest office uplinks.
 */
const PRESIGN_WAVE_MAX_BYTES = 256 * 1024 * 1024;

/** How many times to retry a single file's PUT before marking it failed. */
const PUT_RETRIES = 1;

/**
 * After the main pass, files that still failed (transient errors, or URLs
 * that aged out because a wave ran long) get re-presigned with FRESH URLs and
 * re-uploaded, up to this many rounds. Bounded so a systemic failure — S3
 * outage, revoked credentials — still terminates and reports honestly.
 */
const RECOVERY_ROUNDS = 2;

/**
 * How many times a large file is started over after the server reports its
 * multipart upload gone (GDN-4132). Once: a second loss is not bad luck.
 */
const MULTIPART_RESTARTS = 1;

interface UploadProgress extends UploadCounts {
  percent: number;
}

const isFileEntry = (e: FileSystemEntry): e is FileSystemFileEntry => e.isFile;
const isDirectoryEntry = (e: FileSystemEntry): e is FileSystemDirectoryEntry => e.isDirectory;

/** Read every entry of a directory (readEntries returns at most ~100/call). */
function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const acc: FileSystemEntry[] = [];
    const readBatch = (): void => {
      reader.readEntries(batch => {
        if (batch.length === 0) {
          resolve(acc);
        } else {
          acc.push(...batch);
          readBatch();
        }
      }, reject);
    };
    readBatch();
  });
}

/** Recursively flatten a dropped file/folder entry into files + relative paths. */
async function entryToFiles(entry: FileSystemEntry, prefix: string): Promise<UploadFile[]> {
  if (isFileEntry(entry)) {
    const file = await new Promise<File>((resolve, reject) => entry.file(resolve, reject));
    return [{ file, path: `${prefix}${file.name}` }];
  }
  if (isDirectoryEntry(entry)) {
    const entries = await readAllEntries(entry.createReader());
    const groups = await Promise.all(entries.map(e => entryToFiles(e, `${prefix}${entry.name}/`)));
    return groups.flat();
  }
  return [];
}

/**
 * PUT a single file's bytes directly to its presigned S3 URL. Uses
 * XMLHttpRequest (not fetch) so we can report real upload progress. Sends ONLY
 * the presigned headers + the body — no auth header (the URL carries its auth).
 */
function putFileToS3(
  target: PresignedRawUploadFile,
  file: File,
  onProgress: (loaded: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new UploadCancelledError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', target.url, true);
    for (const [name, value] of Object.entries(target.headers)) {
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = event => {
      if (event.lengthComputable) onProgress(event.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(s3ErrorFromResponse(xhr.status, xhr.responseText));
      }
    };
    // No response at all: CORS, an unreadable (online-only) file, or the network.
    xhr.onerror = () => reject(new S3UploadError(0));
    xhr.onabort = () => reject(new UploadCancelledError());
    const onAbort = () => xhr.abort();
    signal.addEventListener('abort', onAbort, { once: true });
    xhr.addEventListener('loadend', () => signal.removeEventListener('abort', onAbort));
    xhr.send(file);
  });
}

/** Run async tasks with a bounded concurrency pool, preserving call order. */
async function runWithConcurrency(tasks: Array<() => Promise<void>>, limit: number): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (next < tasks.length) {
      const index = next;
      next += 1;
      await tasks[index]();
    }
  });
  await Promise.all(workers);
}

/**
 * Raw source-file upload for the migration agent. Drag & drop files or a whole
 * folder (or browse) — each file is uploaded straight to the S3 landing zone
 * (v2 raw prefix) via a presigned PUT, so the bytes never stream through the app
 * server (which used to time out on large files). Folder structure is preserved.
 * What happens to an upload afterward (convert → stage → commit) shows in the
 * Imports list below.
 */
export function OrganizationDataImportV2Section({
  organizationId,
  onUploaded,
  uploadId,
  embedded = false,
}: OrganizationDataImportV2SectionProps) {
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const uploadInFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  // The last run's failures, kept on screen until dismissed or the next run:
  // a toast alone disappears before staff can read the cause.
  const [outcome, setOutcome] = useState<UploadOutcome | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [presignRawUploads] = usePresignRawUploadsMutation();
  const [refreshInventory] = useRefreshRawUploadInventoryMutation();
  const multipart = useRawMultipartAdapter(organizationId);
  // Cancels the run in flight (GDN-4132). A large file in progress is
  // aborted on the server; small files already sent stay in the upload.
  const cancelRef = useRef<AbortController | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // `webkitdirectory` isn't a typed React attribute — set it imperatively.
  useEffect(() => {
    if (folderInputRef.current) {
      folderInputRef.current.setAttribute('webkitdirectory', '');
      folderInputRef.current.setAttribute('directory', '');
    }
  }, []);

  const uploadItems = useCallback(
    async (items: UploadFile[]) => {
      if (items.length === 0 || !organizationId || uploadInFlight.current) return;
      uploadInFlight.current = true;
      setUploading(true);
      const finishUpload = beginMigrationUpload(organizationId);
      setOutcome(null);
      setProgress({ total: items.length, uploaded: 0, failed: 0, skipped: 0, percent: 0 });
      const cancel = new AbortController();
      cancelRef.current = cancel;
      setCancelling(false);
      let batchId = uploadId;
      let uploadedFiles = 0;
      let duplicatesOnlyCompleted = false;
      let finalOutcome: UploadOutcome | null = null;
      try {
        // Presign + upload in WAVES: URLs expire 15 minutes after minting, so
        // a large drop is presigned a wave at a time and each wave's bytes go
        // up immediately (GDN-2808). Wave 1 mints the batch (or appends to
        // `uploadId` with server-side duplicate skipping); later waves pass
        // `continuation` so the server skips re-enumerating the growing batch.
        const byPath = new Map(items.map(item => [item.path, item.file]));
        const totalBytes = items.reduce((sum, item) => sum + item.file.size, 0);
        // Bytes landed per file (or part-set), kept as a running total: a big
        // drop reports progress for hours, over up to ~120k files.
        const loadedByKey = new Map<string, number>();
        let loadedTotal = 0;
        const setLoaded = (key: string, loaded: number) => {
          loadedTotal += loaded - (loadedByKey.get(key) ?? 0);
          loadedByKey.set(key, loaded);
        };
        // Files that have not landed, by path, with the error that failed
        // them, in the order they first failed. A recovery success removes its
        // file, so this is always "failed right now" and the first entry is
        // the earliest error that still explains something (GDN-4124).
        const failed = new Map<string, unknown>();
        const skippedNames: string[] = [];
        let putsAttempted = false;
        let stopped: UploadStop | undefined;
        let refusal: unknown;
        // Whether this run has opened the batch (a presign or a multipart
        // start succeeded), so later calls against it are continuations.
        let batchOpened = false;
        const cancelled = () => {
          if (!cancel.signal.aborted) return false;
          stopped = { kind: 'cancelled' };
          return true;
        };

        const recordFailure = (path: string, error: unknown) => {
          // Keep the file's first error and its place in the order.
          if (!failed.has(path)) failed.set(path, error);
        };

        // Byte progress arrives many times a second from parallel PUTs; the
        // counts change far less often. Only count changes render at once.
        let lastReport = 0;
        const reportProgress = (bytesOnly = false) => {
          const now = Date.now();
          if (bytesOnly && now - lastReport < 100) return;
          lastReport = now;
          const percent = totalBytes > 0 ? Math.round((loadedTotal / totalBytes) * 100) : 100;
          setProgress({
            total: items.length,
            uploaded: uploadedFiles,
            failed: failed.size,
            skipped: skippedNames.length,
            percent,
          });
        };

        // Waves are capped by file count AND bytes: either bound alone lets a
        // pathological wave (many files, or few huge ones) outlive the URL TTL.
        const buildWaves = (source: UploadFile[]): UploadFile[][] => {
          const result: UploadFile[][] = [];
          let wave: UploadFile[] = [];
          let waveBytes = 0;
          for (const item of source) {
            const overCount = wave.length >= PRESIGN_WAVE_SIZE;
            const overBytes =
              wave.length > 0 && waveBytes + item.file.size > PRESIGN_WAVE_MAX_BYTES;
            if (overCount || overBytes) {
              result.push(wave);
              wave = [];
              waveBytes = 0;
            }
            wave.push(item);
            waveBytes += item.file.size;
          }
          if (wave.length > 0) result.push(wave);
          return result;
        };
        // Files over the threshold go up in parts (GDN-4132): S3 caps one
        // PUT at 5 GB. Everything else keeps the single presigned PUT.
        const large = items.filter(item => shouldUseMultipart(item.file.size));
        const waves = buildWaves(items.filter(item => !shouldUseMultipart(item.file.size)));

        // 1) Presign one wave. The first wave establishes the batch id; every
        // later wave is a continuation against it.
        const presignWave = async (wave: UploadFile[], isFirst: boolean) => {
          const presign = await presignRawUploads({
            organization_id: organizationId,
            files: wave.map(item => ({ filename: item.path })),
            ...(batchId ? { upload_id: batchId } : {}),
            ...(!isFirst ? { continuation: true } : {}),
          }).unwrap();
          batchId = presign.upload_id;
          batchOpened = true;
          for (const name of presign.skipped ?? []) {
            skippedNames.push(name);
            // A skipped file uploads nothing — count its bytes as done so the
            // progress bar still reaches 100%.
            setLoaded(`skipped:${name}`, byPath.get(name)?.size ?? 0);
          }
          // Pair each returned URL with its File (matched by relative path).
          const targets = (presign.files ?? [])
            .map(meta => ({ meta, file: byPath.get(meta.filename) }))
            .filter((t): t is UploadTarget => t.file !== undefined);
          // A requested file with neither a link nor a skip would otherwise
          // drop out of every count.
          const answered = new Set([...targets.map(t => t.meta.filename), ...skippedNames]);
          for (const item of wave) {
            if (!answered.has(item.path)) {
              recordFailure(
                item.path,
                new Error('The server returned no upload link for this file'),
              );
            }
          }
          reportProgress();
          return targets;
        };

        // 2) PUT a wave's bytes directly to S3, tracking real progress.
        const uploadWave = async (targets: UploadTarget[]) => {
          if (targets.length > 0) putsAttempted = true;
          await runWithConcurrency(
            targets.map(t => async () => {
              try {
                for (let attempt = 0; ; attempt += 1) {
                  try {
                    await putFileToS3(
                      t.meta,
                      t.file,
                      loaded => {
                        setLoaded(t.meta.key, loaded);
                        reportProgress(true);
                      },
                      cancel.signal,
                    );
                    break;
                  } catch (err) {
                    if (err instanceof UploadCancelledError || attempt >= PUT_RETRIES) throw err;
                  }
                }
                uploadedFiles += 1;
                failed.delete(t.meta.filename);
                setLoaded(t.meta.key, t.file.size);
              } catch (err) {
                setLoaded(t.meta.key, 0);
                // A PUT cut off by Cancel did not fail: it was not attempted.
                if (!(err instanceof UploadCancelledError)) recordFailure(t.meta.filename, err);
              } finally {
                reportProgress();
              }
            }),
            MAX_CONCURRENT_UPLOADS,
          );
        };

        // A presign the server refuses for a reason no retry cures — above
        // all `batch_not_found`, which means nothing in the batch has landed —
        // is not a per-file failure to repeat for every later wave and every
        // recovery round. Stop, and say why.
        const stopOnRefusal = (err: unknown): boolean => {
          const reason = readPresignBlockReason(err);
          if (!isPermanentPresignRefusal(reason)) return false;
          stopped = { kind: 'refused', reason };
          refusal = err;
          return true;
        };

        // 3) Pipeline: while wave N uploads, wave N+1's URLs are minted, so
        // every URL is used within minutes of signing. Not until something has
        // landed, though: before that a continuation can only be refused, and
        // a first wave that lands nothing ends the run (GDN-4124).
        const prefetch = (wave: UploadFile[]) => {
          const pending = presignWave(wave, false);
          // The real handler is the `await` on the next iteration; this
          // no-op keeps a rejection during the current wave's upload from
          // surfacing as an unhandled-rejection in the meantime.
          pending.catch(() => undefined);
          return pending;
        };
        let nextPresign: Promise<UploadTarget[]> | null =
          waves.length > 0 ? presignWave(waves[0], true) : null;
        for (let i = 0; i < waves.length; i += 1) {
          if (cancelled()) break;
          const pending = nextPresign ?? prefetch(waves[i]);
          nextPresign = null;
          let targets: UploadTarget[] = [];
          try {
            targets = await pending;
          } catch (err) {
            // Before the batch exists nothing has started, so the generic
            // failure toast is accurate — let the outer catch have it. After
            // that, a failed presign RPC is just this wave failing: its files
            // count as failed (cured by recovery) instead of one bad RPC
            // discarding hours of partial progress.
            if (batchId === undefined) throw err;
            if (stopOnRefusal(err)) break;
            for (const item of waves[i]) recordFailure(item.path, err);
            reportProgress();
          }
          if (i + 1 < waves.length && uploadedFiles > 0) nextPresign = prefetch(waves[i + 1]);
          await uploadWave(targets);
          // Nothing has landed in the whole run, after a batch that had
          // targets: the rest would fail the same way. A later batch that
          // fails after earlier ones landed is a partial failure, not this.
          if (targets.length > 0 && uploadedFiles === 0) {
            stopped = { kind: 'landed_nothing' };
            break;
          }
        }

        // 4) Large files, one at a time, each with its parts in parallel. They
        // join the batch the small files opened, or mint it when the drop is
        // all large files. Appending to an existing upload is not a
        // continuation, so the server checks names: a name already there is
        // a duplicate, skipped as a presign would skip it.
        files: for (const item of large) {
          if (stopped || cancelled()) break;
          const progressKey = `multipart:${item.path}`;
          // A file whose multipart upload vanished on the server (aborted,
          // expired, replaced) is started over once, with a plain start. If it
          // had in fact been stored, that start says so (409), and it is done.
          for (let restart = 0; ; restart += 1) {
            try {
              await uploadMultipart({
                file: item.file,
                filename: item.path,
                batch:
                  restart === 0
                    ? { uploadId: batchId, continuation: batchOpened && !uploadId }
                    : { uploadId: batchId, continuation: false },
                adapter: multipart,
                putPart: xhrPutPart,
                signal: cancel.signal,
                onStarted: session => {
                  batchId = session.batchId;
                  batchOpened = true;
                  putsAttempted = true;
                },
                onProgress: loaded => {
                  setLoaded(progressKey, loaded);
                  reportProgress(true);
                },
              });
              uploadedFiles += 1;
              failed.delete(item.path);
              setLoaded(progressKey, item.file.size);
              break;
            } catch (err) {
              setLoaded(progressKey, 0);
              if (err instanceof UploadCancelledError) {
                stopped = { kind: 'cancelled' };
                break files;
              }
              const reason = readPresignBlockReason(err);
              if (reason === 'multipart_upload_not_found' && restart < MULTIPART_RESTARTS) {
                continue;
              }
              if (reason === 'file_already_exists') {
                if (restart > 0) {
                  // Stored by the attempt that looked lost.
                  uploadedFiles += 1;
                  failed.delete(item.path);
                  setLoaded(progressKey, item.file.size);
                } else {
                  skippedNames.push(item.path);
                  setLoaded(`skipped:${item.path}`, item.file.size);
                }
                break;
              }
              // A new upload whose very first call failed never started.
              if (batchId === undefined) throw err;
              recordFailure(item.path, err);
              // A refusal about the batch ends the run; one about this file's
              // own upload (gone, bad parts) fails only this file.
              if (isBatchRefusal(reason) && stopOnRefusal(err)) break files;
              // Nothing has landed in the run: the next file would fail the same way.
              if (uploadedFiles === 0) {
                stopped = { kind: 'landed_nothing' };
                break files;
              }
              break;
            } finally {
              reportProgress();
            }
          }
        }

        // 5) Recovery: anything that failed — a transient error, or a URL
        // that aged out because its wave ran long — gets a FRESH presigned
        // URL and another pass. This, not the presign pipelining, is what
        // guarantees no file is ever abandoned to an expired URL. A run that
        // stopped is not retried: the same failure would repeat.
        for (let round = 0; round < RECOVERY_ROUNDS && !stopped && failed.size > 0; round += 1) {
          if (cancelled()) break;
          // Large files are not retried here: their parts already were, and a
          // single PUT cannot carry them.
          const retryItems = [...failed.keys()].flatMap(path => {
            const file = byPath.get(path);
            return file && !shouldUseMultipart(file.size) ? [{ path, file }] : [];
          });
          for (const wave of buildWaves(retryItems)) {
            if (cancelled()) break;
            try {
              await uploadWave(await presignWave(wave, false));
            } catch (err) {
              // Presign RPC failed for this recovery wave: its files stay
              // failed for the next round (or the final report).
              if (stopOnRefusal(err)) break;
            }
          }
        }

        // A cancel pressed during the last wave has nothing left to stop, but
        // the run still ended on it.
        cancelled();

        // 6) Surface the outcome and refresh the uploads list.
        const skipped = skippedNames.length;
        duplicatesOnlyCompleted = uploadedFiles === 0 && skipped > 0 && failed.size === 0;
        const verb = uploadId ? 'Added' : 'Uploaded';

        const notAttempted = Math.max(0, items.length - uploadedFiles - failed.size - skipped);
        if (failed.size > 0 || stopped || notAttempted > 0) {
          const [earliest] = failed.values();
          const shown = failed.size > 0 ? earliest : refusal;
          finalOutcome = {
            total: items.length,
            uploaded: uploadedFiles,
            failed: failed.size,
            skipped,
            stopped,
            putsAttempted,
            notAttempted,
            firstError: shown === undefined ? undefined : describeUploadError(shown),
            guidance: uploadFailureGuidance(shown),
          };
          enqueueSnackbar(uploadOutcomeHeadline(finalOutcome), {
            variant: stopped?.kind === 'cancelled' && failed.size === 0 ? 'warning' : 'error',
          });
        } else {
          enqueueSnackbar(
            skipped
              ? `${verb} ${uploadedFiles} file(s), skipped ${skipped} duplicate(s)`
              : `${verb} ${uploadedFiles} file(s)`,
            { variant: skipped ? 'warning' : 'success' },
          );
        }
      } catch (err) {
        // The run never started: the first presign of a new upload failed.
        // Keep the reason on screen, not only in a toast.
        finalOutcome = {
          total: items.length,
          uploaded: uploadedFiles,
          failed: 0,
          skipped: 0,
          stopped: { kind: 'not_started' },
          putsAttempted: false,
          notAttempted: items.length - uploadedFiles,
          firstError: describeUploadError(err),
          guidance: [],
        };
        enqueueSnackbar(describeUploadError(err), {
          variant: 'error',
        });
      } finally {
        if (batchId) {
          try {
            await refreshInventory({ organizationId, uploadId: batchId }).unwrap();
          } catch {
            enqueueSnackbar(
              'Files may be uploaded, but file counts could not refresh. Use Refresh in Imports to try again.',
              { variant: 'warning' },
            );
          }
        }
        // Release the shared lock even if a parent's refresh callback fails.
        uploadInFlight.current = false;
        cancelRef.current = null;
        finishUpload();
        if (mounted.current) {
          setUploading(false);
          setProgress(null);
          setOutcome(finalOutcome);
          if (batchId && (uploadedFiles > 0 || duplicatesOnlyCompleted)) {
            try {
              onUploaded?.({ complete: finalOutcome === null });
            } catch {
              enqueueSnackbar('Upload finished. Refresh Imports to see updated counts.', {
                variant: 'warning',
              });
            }
          }
        }
      }
    },
    [
      organizationId,
      uploadId,
      enqueueSnackbar,
      onUploaded,
      presignRawUploads,
      refreshInventory,
      multipart,
    ],
  );

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const dt = e.dataTransfer;
    // Grab entries synchronously — they become invalid after the handler ends.
    const entries: FileSystemEntry[] = [];
    for (const item of Array.from(dt.items)) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry();
        if (entry) entries.push(entry);
      }
    }
    const plainFiles = Array.from(dt.files);
    void (async () => {
      let collected: UploadFile[];
      if (entries.length > 0) {
        const groups = await Promise.all(entries.map(en => entryToFiles(en, '')));
        collected = groups.flat();
      } else {
        collected = plainFiles.map(file => ({ file, path: file.name }));
      }
      await uploadItems(collected);
    })();
  };

  const onPicked = (list: FileList | null) => {
    if (!list) return;
    const collected = Array.from(list).map(file => ({
      file,
      path: file.webkitRelativePath || file.name,
    }));
    void uploadItems(collected);
  };

  const dropZone = (
    <>
      <input
        ref={folderInputRef}
        type="file"
        hidden
        onChange={e => {
          onPicked(e.target.files);
          e.target.value = '';
        }}
      />

      <Box
        onDragOver={e => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={e => {
          e.preventDefault();
          setDragActive(false);
        }}
        onDrop={onDrop}
        sx={{
          border: '2px dashed',
          borderColor: dragActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          bgcolor: dragActive ? 'action.hover' : 'transparent',
        }}
      >
        <CloudUpload color="action" />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Drag &amp; drop files or a folder here
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
            disabled={uploading || !organizationId}
            onClick={() => folderInputRef.current?.click()}
          >
            {uploading ? 'Uploading…' : 'Choose files'}
          </Button>
        </Box>

        {uploading && progress && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress.percent} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {uploadProgressText(progress, progress.percent)}
            </Typography>
            <Button
              size="small"
              sx={{ mt: 0.5 }}
              onClick={() => {
                cancelRef.current?.abort();
                setCancelling(true);
              }}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling…' : 'Cancel upload'}
            </Button>
          </Box>
        )}

        {!uploading && outcome && (
          <RawUploadOutcomeAlert outcome={outcome} onClose={() => setOutcome(null)} />
        )}
      </Box>
    </>
  );

  if (embedded) {
    return dropZone;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Upload source files"
        subheader="Drop source files or a folder here. When the upload finishes, press Go in Imports to start conversion."
      />
      <CardContent>{dropZone}</CardContent>
    </Card>
  );
}
